/**
 * Module Offline Sync — IndexedDB queue + background sync
 * Intercepte les mutations API hors-ligne et les rejoue quand le réseau revient.
 */

const DB_NAME = 'crm-aslane-offline';
const DB_VERSION = 1;
const STORE_QUEUE = 'pending_requests';
const STORE_CACHE = 'api_cache';

let _db = null;

function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'url' });
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

export async function queueOfflineRequest(url, options) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    tx.objectStore(STORE_QUEUE).add({
      url,
      method: options.method || 'POST',
      body: options.body || null,
      headers: options.headers || {},
      createdAt: new Date().toISOString(),
    });
    tx.oncomplete = () => {
      _updateBadge();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function getPendingCount() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_QUEUE, 'readonly');
    const req = tx.objectStore(STORE_QUEUE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(0);
  });
}

export async function syncPendingRequests() {
  const db = await openDB();
  const items = await new Promise((resolve) => {
    const tx = db.transaction(STORE_QUEUE, 'readonly');
    const req = tx.objectStore(STORE_QUEUE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });

  if (items.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;
  const token = localStorage.getItem('auth_token');

  for (const item of items) {
    try {
      const headers = { ...item.headers, 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const csrf = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
      if (csrf) headers['X-CSRF-Token'] = decodeURIComponent(csrf[1]);

      const res = await fetch(item.url, {
        method: item.method,
        headers,
        body: item.body,
      });

      if (res.ok || res.status < 500) {
        await _deleteItem(item.id);
        synced++;
      } else {
        failed++;
      }
    } catch (e) {
      failed++;
    }
  }

  _updateBadge();
  return { synced, failed };
}

async function _deleteItem(id) {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    tx.objectStore(STORE_QUEUE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

function _updateBadge() {
  getPendingCount().then(count => {
    const badge = document.getElementById('offlineSyncBadge');
    if (badge) {
      badge.textContent = count > 0 ? `📡 ${count} en attente` : '';
      badge.style.display = count > 0 ? 'inline-block' : 'none';
    }
  });
}

// Auto-sync when coming back online
window.addEventListener('online', async () => {
  console.log('🌐 Réseau rétabli — synchronisation...');
  const result = await syncPendingRequests();
  if (result.synced > 0) {
    _showSyncToast(`✅ ${result.synced} action(s) synchronisée(s)`);
  }
  if (result.failed > 0) {
    _showSyncToast(`⚠️ ${result.failed} action(s) en erreur`, true);
  }
});

window.addEventListener('offline', () => {
  _showSyncToast('📴 Mode hors-ligne activé — vos actions seront sauvegardées localement', true);
});

// Réceptionner les requêtes mise en queue par le Service Worker
navigator.serviceWorker?.addEventListener('message', async (event) => {
  if (event.data?.type === 'QUEUE_OFFLINE_REQUEST') {
    const { url, method, body, timestamp } = event.data;
    await queueOfflineRequest(url, { method, body, headers: {}, createdAt: new Date(timestamp).toISOString() });
    console.log('[OfflineSync] Requête mise en queue depuis SW:', method, url);
  }
  if (event.data?.type === 'SYNC_REQUESTED') {
    const result = await syncPendingRequests();
    if (result.synced > 0) _showSyncToast(`✅ ${result.synced} action(s) synchronisée(s)`);
  }
});

function _showSyncToast(msg, isWarning = false) {
  let toast = document.getElementById('offlineSyncToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'offlineSyncToast';
    document.body.appendChild(toast);
  }
  toast.style.cssText = `position:fixed;bottom:1rem;left:50%;transform:translateX(-50%);padding:0.6rem 1.2rem;border-radius:10px;font-size:0.85rem;font-weight:700;z-index:99999;box-shadow:0 4px 12px rgba(0,0,0,0.15);transition:opacity 0.4s;
    background:${isWarning ? '#fef3c7' : '#d1fae5'};color:${isWarning ? '#92400e' : '#065f46'};border:1.5px solid ${isWarning ? '#f59e0b' : '#10b981'};`;
  toast.textContent = msg;
  toast.style.opacity = '1';
  setTimeout(() => { toast.style.opacity = '0'; }, 4000);
}

export function initOfflineUI() {
  if (!document.getElementById('offlineSyncBadge')) {
    const badge = document.createElement('span');
    badge.id = 'offlineSyncBadge';
    badge.style.cssText = 'position:fixed;top:0.5rem;right:0.5rem;padding:0.3rem 0.7rem;border-radius:8px;background:#fef3c7;color:#92400e;font-size:0.75rem;font-weight:700;z-index:99998;display:none;border:1.5px solid #f59e0b;';
    document.body.appendChild(badge);
  }
  _updateBadge();
}
